import { ReactNode, Suspense, lazy, type ComponentType } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { useUpdateReady, applyUpdate } from "./lib/update";
import { Icon } from "./components/Icons";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Welcome from "./Welcome";
import Join from "./Join";
const Access = lazy(() => import("./Access"));
const Account = lazy(() => import("./Account"));
const ImportFromAtelier = lazy(() => import("./ImportFromAtelier"));
const Guide = lazy(() => import("./Guide"));
const SupplierQuotePage = lazy(() => import("./portal/SupplierQuotePage"));
const DrawingPackagePage = lazy(() => import("./portal/DrawingPackagePage"));
const ExecDashboardPage = lazy(() => import("./portal/ExecDashboardPage"));
import { Shell } from "./components/Shell";
import { PAYMENTS_ONLY_PRINCIPAL } from "./lib/paymentsGate";
// the project shell loads with the first project page, not on the landing screen
const PmShell = lazy(() => import("./pm/PmShell").then((m) => {
  // a mangled module (served from a poisoned cache) arrives without its
  // export — throw the phrase the stale-chunk recovery listens for
  if (!m?.PmShell) throw new Error("stale page module: PmShell");
  return { default: m.PmShell };
}));
import { useSession, pmFullAccess } from "./lib/auth";
import { usePmStore, contractorProjects, clientProjects } from "./lib/pmStore";
import { useStore, clientTenders, bidderTenders } from "./lib/store";
import { UploadOverlay } from "./components/UploadOverlay";
const Dashboard = lazy(() => import("./manager/Dashboard"));
const Invitation = lazy(() => import("./manager/Invitation"));
const Timeline = lazy(() => import("./manager/Timeline"));
const Documents = lazy(() => import("./manager/Documents"));
const Contract = lazy(() => import("./manager/Contract"));
const Quotes = lazy(() => import("./manager/Quotes"));
const Bidders = lazy(() => import("./manager/Bidders"));
const Submissions = lazy(() => import("./manager/Submissions"));
const Rfis = lazy(() => import("./manager/Rfis"));
const Results = lazy(() => import("./manager/Results"));
const Alerts = lazy(() => import("./manager/Alerts"));
const BidTeam = lazy(() => import("./manager/Team"));
const TenderList = lazy(() => import("./manager/TenderList"));
const ClientPortal = lazy(() => import("./portal/ClientPortal"));
const BidderPortal = lazy(() => import("./portal/BidderPortal"));
const ProjectClientPortal = lazy(() => import("./portal/ProjectClientPortal"));
const SubPortal = lazy(() => import("./portal/SubPortal"));
const ProjectList = lazy(() => import("./pm/ProjectList"));
const ContractorProjects = lazy(() => import("./pm/ContractorProjects"));
const Portfolio = lazy(() => import("./pm/Portfolio"));
const CommsHub = lazy(() => import("./pm/CommsHub"));
const PmDashboard = lazy(() => import("./pm/Dashboard"));
const Drawings = lazy(() => import("./pm/Drawings"));
const Program = lazy(() => import("./pm/Program"));
const Analytics = lazy(() => import("./pm/Analytics"));
const Forecast = lazy(() => import("./pm/Forecast"));
const Snapshots = lazy(() => import("./pm/Snapshots"));
const Labour = lazy(() => import("./pm/Labour"));
const Procurement = lazy(() => import("./pm/Procurement"));
const Targets = lazy(() => import("./pm/Targets"));
const TasksPage = lazy(() => import("./pm/TasksPage"));
const Inbox = lazy(() => import("./pm/Inbox"));
const Audit = lazy(() => import("./pm/Audit"));
const ProjectBrain = lazy(() => import("./pm/ProjectBrain"));
const PhotoProgress = lazy(() => import("./pm/PhotoProgress"));
const DailyLogPage = lazy(() => import("./pm/DailyLogPage"));
const Field = lazy(() => import("./pm/Field"));
const Voice = lazy(() => import("./pm/Voice"));
const Coordination = lazy(() => import("./pm/Coordination"));
const Recovery = lazy(() => import("./pm/Recovery"));
const PmRfis = lazy(() => import("./pm/PmRfis"));
const Bottlenecks = lazy(() => import("./pm/Bottlenecks"));
const LongLead = lazy(() => import("./pm/LongLead"));
const Models = lazy(() => import("./pm/Models"));
const Twin = lazy(() => import("./pm/Twin"));
const Deliveries = lazy(() => import("./pm/Deliveries"));
const Valuations = lazy(() => import("./pm/Valuations"));
const Boq = lazy(() => import("./pm/Boq"));
const Packages = lazy(() => import("./pm/Packages"));
const Safety = lazy(() => import("./pm/Safety"));
const Cost = lazy(() => import("./pm/Cost"));
const Cashflow = lazy(() => import("./pm/Cashflow"));
const Currency = lazy(() => import("./pm/Currency"));
const Templates = lazy(() => import("./pm/Templates"));
const Telemetry = lazy(() => import("./pm/Telemetry"));
const Learnings = lazy(() => import("./pm/Learnings"));
const WarRoom = lazy(() => import("./pm/WarRoom"));
const PmDocuments = lazy(() => import("./pm/Documents"));
const CadFiles = lazy(() => import("./pm/CadFiles"));
const Submittals = lazy(() => import("./pm/Submittals"));
const CorrespondencePage = lazy(() => import("./pm/Correspondence"));
const Delays = lazy(() => import("./pm/Delays"));
const ContractClauses = lazy(() => import("./pm/ContractClauses"));
const Collaboration = lazy(() => import("./pm/Collaboration"));
const Variations = lazy(() => import("./pm/Variations"));
const Approvals = lazy(() => import("./pm/Approvals"));
const Inspections = lazy(() => import("./pm/Inspections"));
const Materials = lazy(() => import("./pm/Materials"));
const Inventory = lazy(() => import("./pm/Inventory"));
const Expenses = lazy(() => import("./pm/Expenses"));
const Punch = lazy(() => import("./pm/Punch"));
const Mep = lazy(() => import("./pm/Mep"));
const Reports = lazy(() => import("./pm/Reports"));
const Team = lazy(() => import("./pm/Team"));
const Scorecards = lazy(() => import("./pm/Scorecards"));
const Handover = lazy(() => import("./pm/Handover"));
const Gallery = lazy(() => import("./pm/Gallery"));
const Observations = lazy(() => import("./pm/Observations"));
const SiteVisits = lazy(() => import("./pm/SiteVisits"));
const ClientControl = lazy(() => import("./pm/ClientControl"));
const Meetings = lazy(() => import("./pm/Meetings"));
const Renders = lazy(() => import("./pm/Renders"));

const SECTION_MAP: Record<string, ComponentType> = {
  dashboard: Dashboard,
  invitation: Invitation,
  timeline: Timeline,
  documents: Documents,
  contract: Contract,
  quotes: Quotes,
  bidders: Bidders,
  submissions: Submissions,
  rfis: Rfis,
  results: Results,
  alerts: Alerts,
  team: BidTeam,
};

const PM_SECTION_MAP: Record<string, ComponentType> = {
  dashboard: PmDashboard,
  inbox: Inbox,
  audit: Audit,
  brain: ProjectBrain,
  photoprogress: PhotoProgress,
  tasks: TasksPage,
  drawings: Drawings,
  cad: CadFiles,
  models: Models,
  twin: Twin,
  program: Program,
  analytics: Analytics,
  forecast: Forecast,
  snapshots: Snapshots,
  labour: Labour,
  procurement: Procurement,
  targets: Targets,
  dailylog: DailyLogPage,
  field: Field,
  voice: Voice,
  coordination: Coordination,
  recovery: Recovery,
  rfis: PmRfis,
  bottlenecks: Bottlenecks,
  longlead: LongLead,
  submittals: Submittals,
  correspondence: CorrespondencePage,
  delays: Delays,
  contract: ContractClauses,
  instructions: () => <CorrespondencePage focus="instructions" />,
  collaboration: Collaboration,
  variations: Variations,
  approvals: Approvals,
  inspections: Inspections,
  valuations: Valuations,
  boq: Boq,
  packages: Packages,
  safety: Safety,
  cost: Cost,
  cashflow: Cashflow,
  currency: Currency,
  templates: Templates,
  telemetry: Telemetry,
  learnings: Learnings,
  warroom: WarRoom,
  documents: PmDocuments,
  materials: Materials,
  inventory: Inventory,
  expenses: Expenses,
  punch: Punch,
  mep: Mep,
  reports: Reports,
  deliveries: Deliveries,
  team: Team,
  scorecards: Scorecards,
  handover: Handover,
  photos: Gallery,
  observations: Observations,
  sitevisits: SiteVisits,
  clientportal: ClientControl,
  meetings: Meetings,
  renders: Renders,
};

/* --- guards ------------------------------------------------------------ */
function RequireManager({ children }: { children: ReactNode }) {
  const s = useSession();
  if (!s || s.role !== "manager") return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** The tender cockpit: the manager everywhere, tender-team members in their
    own tender only (page-level actions gate on their role). */
function RequireBidCockpit({ children }: { children: ReactNode }) {
  const s = useSession();
  const { tid } = useParams();
  // studio-level tender roles (tenderId "") work across every tender room
  const ok = s && (s.role === "manager" || (s.role === "bidteam" && (s.tenderId === tid || s.tenderId === "")));
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Supplier's tokened quotation room — public route, token IS the auth. */
function SupplierQuoteRoute() {
  const { token = "" } = useParams();
  return <SupplierQuotePage token={token} />;
}

/** The employer's live executive dashboard — public route, token IS the auth. */
function ExecRoute() {
  const { token = "" } = useParams();
  return <ExecDashboardPage token={token} />;
}

/** Tokened drawing package — public route, token IS the auth. */
function DrawingPackageRoute() {
  const { token = "" } = useParams();
  return <DrawingPackagePage token={token} />;
}

/** /manage — tender list for the manager and studio-level tender roles;
    per-tender team members land straight in their tender. */
function BidHome() {
  const s = useSession();
  if (s?.role === "bidteam") {
    return s.tenderId ? <Navigate to={`/manage/${s.tenderId}/dashboard`} replace /> : <TenderList />;
  }
  return <RequireManager><TenderList /></RequireManager>;
}

function RequireClient({ children }: { children: ReactNode }) {
  const s = useSession();
  const { tid } = useParams();
  const { tenders } = useStore();
  // one login opens every tender this client belongs to — matched by
  // name+company, the same way a contractor reaches all their projects
  const ok = s && (s.role === "manager" ||
    (s.role === "client" && (s.tenderId === tid || clientTenders(s, tenders).some((t) => t.id === tid))));
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireBidder({ children }: { children: ReactNode }) {
  const s = useSession();
  const { tid } = useParams();
  const { tenders } = useStore();
  // the firm's one login opens every tender they're invited to
  const ok = s && (s.role === "manager" ||
    (s.role === "bidder" && (s.tenderId === tid || bidderTenders(s, tenders).some((t) => t.id === tid))));
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequirePm({ children }: { children: ReactNode }) {
  const s = useSession();
  const { pid } = useParams();
  const { projects } = usePmStore();
  // Studio and Site Admins work in every project room; a contractor reaches the
  // project their code belongs to AND any other project they're a member of
  // (matched by name+company) — so someone on several jobs can switch between.
  const ok = pmFullAccess(s) ||
    (s?.role === "contractor" && (s.projectId === pid || contractorProjects(s, projects).some((p) => p.id === pid)));
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequirePmFull({ children }: { children: ReactNode }) {
  if (!pmFullAccess(useSession())) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** The contractor project picker: any PM party (full-access or a contractor). */
function RequirePmAny({ children }: { children: ReactNode }) {
  const s = useSession();
  if (!(pmFullAccess(s) || s?.role === "contractor")) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** The subcontract room: the sub whose code belongs to this package, or the
    studio previewing what the sub sees. */
function RequireSub({ children }: { children: ReactNode }) {
  const s = useSession();
  const { pid, pkid } = useParams();
  const ok = pmFullAccess(s) ||
    (s?.role === "sub" && s.projectId === pid && s.packageId === pkid);
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequirePmClient({ children }: { children: ReactNode }) {
  const s = useSession();
  const { pid } = useParams();
  const { projects } = usePmStore();
  // same principle on the project side: the passcode signed them in once; the
  // guard opens every project where that same person is the client
  const ok = s && (s.role === "manager" ||
    (s.role === "pclient" && (s.projectId === pid || clientProjects(s, projects).some((p) => p.id === pid))));
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ManagerSection() {
  const { section = "dashboard" } = useParams();
  const Component = SECTION_MAP[section] ?? Dashboard;
  return <Shell><Component /></Shell>;
}

/** Shown for the moment a page's code is fetched — each page now loads on
    demand instead of all sixty-five arriving with the first visit. */
function PageLoading({ inline }: { inline?: boolean }) {
  return (
    <div style={{ minHeight: inline ? 240 : "100vh", display: "grid", placeItems: "center" }}>
      <span className="dim" style={{ fontSize: 13 }}>Loading…</span>
    </div>
  );
}

function PmSection() {
  const { section = "dashboard", pid = "" } = useParams();
  const session = useSession();
  // payment information is the principal's alone — a typed URL gets the dashboard
  const allowed = !PAYMENTS_ONLY_PRINCIPAL.has(section) || session?.role === "manager";
  const Component = allowed ? (PM_SECTION_MAP[section] ?? PmDashboard) : PmDashboard;
  // one page failing keeps the menu working; moving to another page clears it
  return <PmShell><ErrorBoundary inline key={`${pid}/${section}`}><Suspense fallback={<PageLoading inline />}><Component /></Suspense></ErrorBoundary></PmShell>;
}

/** Floating "a new version is deployed" pill — one tap swaps in the update. */
function UpdatePill() {
  const ready = useUpdateReady();
  if (!ready) return null;
  return (
    <button
      onClick={() => void applyUpdate()}
      className="row"
      style={{
        position: "fixed", bottom: "calc(84px + env(safe-area-inset-bottom))", right: 18, zIndex: 120,
        gap: 8, padding: "10px 16px", borderRadius: 40, border: "1px solid var(--line)",
        background: "var(--ink)", color: "var(--paper)", cursor: "pointer",
        boxShadow: "0 12px 34px -10px rgba(0,0,0,.45)", fontSize: 13, fontWeight: 500,
        animation: "floatIn .25s ease",
      }}
    >
      <Icon.spark width={14} style={{ color: "var(--accent)" }} />
      Update ready — refresh
    </button>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <UploadOverlay />
      <UpdatePill />
      <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/join" element={<Join />} />
        <Route path="/import" element={<ImportFromAtelier />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/quote/:token" element={<SupplierQuoteRoute />} />
        <Route path="/pkg/:token" element={<DrawingPackageRoute />} />
        <Route path="/exec/:token" element={<ExecRoute />} />
        <Route path="/access" element={<RequireManager><Access /></RequireManager>} />
        <Route path="/account" element={<RequireManager><Account /></RequireManager>} />
        {/* Bid Manager */}
        <Route path="/manage" element={<BidHome />} />
        <Route path="/manage/:tid" element={<RequireBidCockpit><ManagerSection /></RequireBidCockpit>} />
        <Route path="/manage/:tid/:section" element={<RequireBidCockpit><ManagerSection /></RequireBidCockpit>} />
        <Route path="/client/:tid" element={<RequireClient><ClientPortal /></RequireClient>} />
        <Route path="/bid/:tid" element={<RequireBidder><BidderPortal /></RequireBidder>} />
        {/* Project Manager */}
        <Route path="/pm/select" element={<RequirePmAny><ContractorProjects /></RequirePmAny>} />
        <Route path="/pm/portfolio" element={<RequirePmAny><Portfolio /></RequirePmAny>} />
        <Route path="/pm/comms" element={<RequirePmFull><CommsHub /></RequirePmFull>} />
        <Route path="/pm" element={<RequirePmFull><ProjectList /></RequirePmFull>} />
        <Route path="/pm/:pid" element={<RequirePm><PmSection /></RequirePm>} />
        <Route path="/pm/:pid/:section" element={<RequirePm><PmSection /></RequirePm>} />
        <Route path="/site/:pid" element={<RequirePmClient><ProjectClientPortal /></RequirePmClient>} />
        <Route path="/sub/:pid/:pkid" element={<RequireSub><SubPortal /></RequireSub>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
